/**
 * PricingFeatureMatrix — the capability comparison across every tier, rendered
 * from the shared pricing.json `features` matrix (Rule 17 — the same source the
 * start app and Stripe entitlements use; nothing hardcoded in the marketing
 * layer). Rows are grouped by category; `switch` rows show a tick / dash,
 * `quantity` rows show their value. Enterprise + Sovereign columns are tinted
 * so the differentiators stand out. Composes tokens only — no bespoke CSS.
 */
import { Fragment } from 'react';
import { Check, Minus } from 'lucide-react';
import rawPricing from '../../data/pricing.json';
import { featureColumns, type RawPricingResponse } from '../../data/pricing-transform';
import { CollapsibleTable } from './CollapsibleTable';
import { cn } from '../../utils';

const RAW = rawPricing as RawPricingResponse;
const HIGHLIGHT = new Set(['enterprise', 'sovereign']);
// quantity values that read as an unlocked capability rather than a plain count
const EMPHASISE = new Set(['Unlimited', 'Included', 'Available']);

export interface PricingFeatureMatrixProps {
  /** Optional intro caption under the table. */
  readonly note?: string;
  /** Start fully expanded (default false → peek the first rows). */
  readonly defaultOpen?: boolean;
}

export function PricingFeatureMatrix({ note, defaultOpen = false }: PricingFeatureMatrixProps) {
  const cols = featureColumns(RAW);
  const rowCount = RAW.features.reduce((n, c) => n + c.rows.length, 0);

  return (
    <div className="my-8">
      <CollapsibleTable
        title="Compare capabilities across tiers"
        summary={`${rowCount} capabilities · ${cols.length} tiers`}
        defaultOpen={defaultOpen}
      >
        <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-muted/50">
              <th className="p-3 text-left font-display font-semibold text-foreground">Capability</th>
              {cols.map((c) => (
                <th
                  key={c.key}
                  className={cn(
                    'p-3 text-center font-display font-semibold',
                    HIGHLIGHT.has(c.key) ? 'bg-primary-light text-primary' : 'text-foreground',
                  )}
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {RAW.features.map((cat) => (
              <Fragment key={cat.category}>
                <tr>
                  <td
                    colSpan={cols.length + 1}
                    className="border-t border-border bg-muted/40 px-3 py-2 font-mono text-xs uppercase tracking-wider text-muted-foreground"
                  >
                    {cat.category}
                  </td>
                </tr>
                {cat.rows.map((row) => (
                  <tr key={row.key} className="border-t border-border">
                    <td className="p-3 text-foreground">{row.feature}</td>
                    {cols.map((c) => {
                      const v = row.tiers[c.key];
                      const tint = HIGHLIGHT.has(c.key) ? 'bg-primary-light/40' : '';
                      return (
                        <td key={c.key} className={cn('p-3 text-center', tint)}>
                          {row.type === 'switch' ? (
                            v === true ? (
                              <Check className="mx-auto size-4 text-success" aria-label="Included" />
                            ) : (
                              <Minus className="mx-auto size-4 text-muted-foreground/40" aria-label="Not included" />
                            )
                          ) : (
                            <span className={cn(EMPHASISE.has(String(v)) ? 'font-medium text-primary' : 'text-foreground')}>
                              {String(v)}
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
        </div>
      </CollapsibleTable>
      {note && <p className="mt-3 text-center text-xs text-muted-foreground">{note}</p>}
    </div>
  );
}
